import * as i0 from '@angular/core';
import { InjectionToken, NgModule, Injectable, Inject, Optional } from '@angular/core';
import * as i1 from '@angular/fire/auth';
import { authState, createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, FacebookAuthProvider, signInWithEmailAndPassword, signOut } from '@angular/fire/auth';
import { ReplaySubject, of, from, mergeMap } from 'rxjs';
import { switchMap, startWith, tap } from 'rxjs/operators';
import { AuthUserState, AuthUser, Organization, RoleClaim } from '@frogconnexion/core-common';
import * as i2 from '@angular/router';

function unsubscribe(...subs) {
    if (!subs || subs.length === 0) {
        return;
    }
    subs.forEach(s => {
        if (s) {
            s.unsubscribe();
        }
    });
}

var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["VERBOSE"] = 0] = "VERBOSE";
    LogLevel[LogLevel["DEBUG"] = 1] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 2] = "INFO";
    LogLevel[LogLevel["WARNING"] = 3] = "WARNING";
    LogLevel[LogLevel["ERROR"] = 4] = "ERROR";
})(LogLevel || (LogLevel = {}));
function parse(config) {
    return {
        level: parseLevel(config.level),
        toast: config.toast
    };
}
function parseLevel(level) {
    if (level === 'string') {
        switch (level.toLowerCase()) {
            case "verbose":
                return LogLevel.VERBOSE;
            case "debug":
                return LogLevel.DEBUG;
            case "info":
                return LogLevel.INFO;
            case "warning":
                return LogLevel.WARNING;
            case "error":
                return LogLevel.ERROR;
        }
        return LogLevel.INFO;
    }
    return level;
}

const AUTH_CONFIG = new InjectionToken('AuthConfig');
const DEFAULT_AUTH_CONFIG = {
    namespace: 'frog'
};
const LOGGER_CONFIG = new InjectionToken('LoggerConfig');
const DEFAULT_LOGGER_CONFIG = {
    level: LogLevel.VERBOSE,
    toast: false
};
class FirebaseAuthModule {
    static forRoot(config) {
        return {
            ngModule: FirebaseAuthModule,
            providers: [
                {
                    provide: AUTH_CONFIG,
                    useValue: {
                        default: DEFAULT_AUTH_CONFIG,
                        config: { namespace: config.namespace },
                    },
                },
                {
                    provide: LOGGER_CONFIG,
                    useValue: {
                        default: DEFAULT_LOGGER_CONFIG,
                        config: {
                            level: parseLevel(config.log.level),
                            toast: config.log.toast
                        }
                    }
                }
            ]
        };
    }
}
FirebaseAuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: FirebaseAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FirebaseAuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.5", ngImport: i0, type: FirebaseAuthModule });
FirebaseAuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: FirebaseAuthModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: FirebaseAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: []
                }]
        }] });

class LoggerService {
    constructor(logConfig) {
        this.logConfig = logConfig;
    }
    static getLogLevel(level) {
        return `[${LogLevel[level]}]`;
    }
    loggingOnLevel(level, consoleFunc, ...information) {
        if (this.logConfig.level <= level) {
            consoleFunc(LoggerService.getLogLevel(level), ...information);
        }
    }
    verbose(...information) {
        this.loggingOnLevel(LogLevel.VERBOSE, console.log, information);
    }
    debug(...information) {
        this.loggingOnLevel(LogLevel.DEBUG, console.log, information);
    }
    info(...information) {
        this.loggingOnLevel(LogLevel.INFO, console.info || console.log, information);
    }
    warn(...information) {
        this.loggingOnLevel(LogLevel.WARNING, console.warn || console.log, information);
    }
    error(...information) {
        this.loggingOnLevel(LogLevel.ERROR, console.error, information);
    }
}
LoggerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: LoggerService, deps: [{ token: LOGGER_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
LoggerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: LoggerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: LoggerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_CONFIG]
                }] }]; } });

class FirebaseAuthService {
    constructor(authConfig, auth, router, logger) {
        this.authConfig = authConfig;
        this.auth = auth;
        this.router = router;
        this.logger = logger;
        this.latestAuthState = null;
        this.logger.info('Creating new Auth Service');
        this.currentUserSubject = new ReplaySubject(1);
        this.idTokenSubject = new ReplaySubject(1);
        // Everytime firebase has a new auth state, it will trigger this action here.
        // Does it handle disconnects? Maybe let's see later
        this.subscription = authState(this.auth)
            .pipe(switchMap((as) => {
            this.logger.debug('Received user from authState', as);
            this.latestAuthState = as || null;
            if (!as) {
                this.logger.debug('User from authState is null. Set to anonymous');
                // We have checked and there are no logged in users for now. Set to anonymous
                const user = { user: this.getNotAuthenticatedDummyUser(AuthUserState.COMPLETE), idToken: null };
                return of(user);
            }
            this.logger.debug('User from authState is not null. Refreshing token');
            // There is one logged in user. Fetch his metadata and his idtoken
            return this.refresh(as, false);
        }))
            .pipe(startWith((JSON.parse(localStorage.getItem(`${this.authConfig.namespace}:user`) || "{}") || {
            user: this.getNotAuthenticatedDummyUser(AuthUserState.COMPLETE),
            idToken: null
        })))
            .subscribe((result) => {
            this.publishAuth(result);
        });
    }
    ngOnDestroy() {
        unsubscribe(this.subscription);
    }
    getNotAuthenticatedDummyUser(state) {
        const user = AuthUser.anonymousUnauthenticated();
        user.state = state;
        return user;
    }
    currentUserObservable() {
        return this.currentUserSubject;
    }
    currentIdToken() {
        return this.idTokenSubject;
    }
    refreshFromAuthState() {
        return this.refresh(this.latestAuthState);
    }
    refresh(as, publishAfterRefresh = true) {
        return from(this.refreshUserDetails(as))
            .pipe(tap((result) => {
            localStorage.setItem(`${this.authConfig.namespace}:user`, JSON.stringify(result));
            if (publishAfterRefresh) {
                this.publishAuth(result);
            }
        }));
    }
    publishAuth(result) {
        if (result) {
            this.logger.info('Auth Published');
            this.logger.info(result);
            this.idTokenSubject.next(result.idToken);
            this.currentUserSubject.next(AuthUser.fromObject(result.user));
        }
        else {
            this.logger.info('Null Auth Published');
            this.idTokenSubject.next(null);
            this.currentUserSubject.next(null);
        }
    }
    async refreshUserDetails(as) {
        const idToken = await as.getIdTokenResult(true);
        const user = AuthUser.fromIam(as, idToken.claims);
        this.idTokenSubject.next(idToken.token);
        return { user: user, idToken: idToken.token };
    }
    registerUser(email, password) {
        return createUserWithEmailAndPassword(this.auth, email, password)
            .then((result) => {
            this.logger.debug('User created in IAM');
            return result;
        });
    }
    googleSignIn() {
        return signInWithPopup(this.auth, new GoogleAuthProvider());
    }
    facebookSignIn() {
        return signInWithPopup(this.auth, new FacebookAuthProvider());
    }
    emailSignIn(email, password) {
        return signInWithEmailAndPassword(this.auth, email, password)
            .catch(error => {
            this.logger.error(error);
            throw error;
        });
    }
    signOut() {
        localStorage.removeItem(`${this.authConfig.namespace}:user`);
        return signOut(this.auth)
            .then(() => {
            this.idTokenSubject.next(null);
            this.currentUserSubject.next(this.getNotAuthenticatedDummyUser(AuthUserState.NEEDS_REFRESH));
        });
    }
}
FirebaseAuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: FirebaseAuthService, deps: [{ token: AUTH_CONFIG }, { token: i1.Auth, optional: true }, { token: i2.Router }, { token: LoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
FirebaseAuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: FirebaseAuthService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: FirebaseAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: i1.Auth, decorators: [{
                    type: Optional
                }] }, { type: i2.Router }, { type: LoggerService }]; } });

class OrganizationStorageService {
    constructor(authConfig, logger) {
        this.authConfig = authConfig;
        this.logger = logger;
        this.organizationSubject = new ReplaySubject(1);
    }
    ngOnDestroy() {
        this.organizationSubject.complete();
    }
    get current$() {
        return this.organizationSubject;
    }
    set(o) {
        this.logger.debug(`Updating organization to ${o?.organization || null}`);
        if (!o) {
            this.clear();
            this.organizationSubject.next(null);
        }
        else {
            localStorage.setItem(`${this.authConfig.namespace}:org`, JSON.stringify(o.toHttp()));
            this.organizationSubject.next(o);
        }
    }
    get() {
        const oStr = localStorage.getItem(`${this.authConfig.namespace}:org`);
        if (!oStr) {
            return null;
        }
        const o = JSON.parse(oStr);
        return Organization.fromHttp(o) || null;
    }
    clear() {
        localStorage.removeItem(`${this.authConfig.namespace}:org`);
    }
}
OrganizationStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: OrganizationStorageService, deps: [{ token: AUTH_CONFIG }, { token: LoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
OrganizationStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: OrganizationStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: OrganizationStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: LoggerService }]; } });

class AuthGuard {
    constructor(authConfig, authService, organizationStorageService, router, logger) {
        this.authConfig = authConfig;
        this.authService = authService;
        this.organizationStorageService = organizationStorageService;
        this.router = router;
        this.logger = logger;
    }
    canActivate() {
        const organization = this.organizationStorageService.get();
        const roles = this.authConfig.authorizedRoles;
        // If role-based auth is enabled / desired
        if (roles && roles?.length > 0) {
            return this.canActivateWithRoles(organization?.organization || null, roles);
        }
        return this.authService.currentUserObservable().pipe(mergeMap(user => {
            if (!user || !user.authenticated) {
                this.logger.debug('Auth Guard Failed. Authenticating first.');
                this.router.navigate(['/login']);
                return of(false);
            }
            this.logger.debug('Auth Guard Succeeded with user', user);
            return of(true);
        }));
    }
    canActivateWithRoles(organization, roles) {
        return this.authService.currentUserObservable().pipe(mergeMap(user => {
            if (!user || !user.authenticated) {
                this.logger.debug('Auth Guard Failed. Authenticating first.');
                this.router.navigate([`/login`]);
                return of(false);
            }
            if (user.anonymous) {
                this.logger.debug('Auth Guard Failed. Anonymous user.');
                this.router.navigate([`/login`]);
                return of(false);
            }
            if (!user.hasAnyRole(roles.map((r) => RoleClaim.parse(r, organization || undefined)))) {
                this.logger.debug(`Auth Guard Failed. User: ${user.uid} - ${user.email} doesn\'t have the appropriate role for current organization ${organization}.`);
                this.router.navigate([`/forbidden`]);
                return of(false);
            }
            this.logger.debug('Auth Guard Success.');
            return of(true);
        }));
    }
}
AuthGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: AuthGuard, deps: [{ token: AUTH_CONFIG }, { token: FirebaseAuthService }, { token: OrganizationStorageService }, { token: i2.Router }, { token: LoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: AuthGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.5", ngImport: i0, type: AuthGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: FirebaseAuthService }, { type: OrganizationStorageService }, { type: i2.Router }, { type: LoggerService }]; } });

/*
 * Public API Surface of auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_CONFIG, AuthGuard, DEFAULT_AUTH_CONFIG, DEFAULT_LOGGER_CONFIG, FirebaseAuthModule, FirebaseAuthService, LOGGER_CONFIG, LoggerService, OrganizationStorageService, unsubscribe };
